import * as React from 'react';
import { Query } from 'react-apollo';
import { Link } from 'react-router-dom';

import { LIVE_ORDER } from 'src/app/constants/route';
import { toCountFormat, toPercentageFormat } from './Dashboard.formatters';
import {
  DashboardTodaySnapshot,
  FETCH_DASHBOARD_TODAY_SNAPSHOT_QUERY,
  FetchDashboardTodaySnapshotQueryResult
} from './Dashboard.queries';

interface TodaySnapshotCardProps {
  branchId?: number;
}

interface SnapshotRowProps {
  count: number;
  icon: string;
  label: string;
  modifier: string;
  total: number;
}

/**
 * Matches the 60s server-side cache TTL for this query — polling faster only adds traffic
 * without producing fresher numbers.
 */
const POLL_INTERVAL_IN_MS = 60000;

const SnapshotRow = (props: SnapshotRowProps) => {
  const { count, icon, label, modifier, total } = props;
  const share = total > 0 ? (count / total) * 100 : 0;

  return (
    <div className="Dashboard__snapshot-row">
      <span className={`Dashboard__snapshot-icon Dashboard__snapshot-icon--${modifier}`}>{icon}</span>
      <span className="Dashboard__snapshot-label">{label}</span>
      <span className="Dashboard__snapshot-value">
        {toCountFormat(count)}
        <span className={`Dashboard__snapshot-share Dashboard__snapshot-share--${modifier}`}>
          ({toPercentageFormat(share)})
        </span>
      </span>
    </div>
  );
};

export const TodaySnapshotCard = (props: TodaySnapshotCardProps) => (
  <div className="Dashboard__card Dashboard__snapshot">
    <div className="Dashboard__card-header">
      <div>
        <h3 className="Dashboard__card-title">Today&apos;s Delivery Snapshot</h3>
        {/* This card always reports today, unlike every other section on the page. */}
        <p className="Dashboard__card-subtitle">Today &middot; outside period filter</p>
      </div>
      <span className="Dashboard__live-badge">Live</span>
    </div>

    <Query<FetchDashboardTodaySnapshotQueryResult>
      fetchPolicy="network-only"
      pollInterval={POLL_INTERVAL_IN_MS}
      query={FETCH_DASHBOARD_TODAY_SNAPSHOT_QUERY}
      variables={{ branchId: props.branchId }}
    >
      {({ loading, error, data }) => {
        if (loading && (!data || !data.dashboardTodaySnapshot))
          return <div className="Dashboard__loading">Loading snapshot...</div>;
        if (error) return <div className="Dashboard__error">Failed to load snapshot</div>;
        if (!data || !data.dashboardTodaySnapshot)
          return <div className="Dashboard__empty">No data available for today</div>;

        const snapshot: DashboardTodaySnapshot = data.dashboardTodaySnapshot;
        const total = snapshot.totalScheduledOrders;

        return (
          <div className="Dashboard__snapshot-body">
            <div className="Dashboard__snapshot-row Dashboard__snapshot-row--total">
              <span className="Dashboard__snapshot-icon Dashboard__snapshot-icon--total">📋</span>
              <span className="Dashboard__snapshot-label">Total Scheduled Orders</span>
              <span className="Dashboard__snapshot-value">{toCountFormat(total)}</span>
            </div>

            {/*
              Labelled "Delivered", not "Delivered On Time": an order sitting in today's queue may
              have been rolled over from an earlier promised date, so finishing it today does not
              make it on time. Lateness is reported in the Delivery Timeliness card instead.
            */}
            <SnapshotRow count={snapshot.deliveredOnTime} icon="✓" label="Delivered" modifier="success" total={total} />
            <SnapshotRow
              count={snapshot.pendingToday}
              icon="◷"
              label="Pending (Today)"
              modifier="warning"
              total={total}
            />
            <SnapshotRow
              count={snapshot.unfinished}
              icon="!"
              label="Unfinished (Not Started)"
              modifier="danger"
              total={total}
            />

            <Link className="Dashboard__snapshot-link" to={LIVE_ORDER.path}>
              Go to Live Order Monitoring →
            </Link>
          </div>
        );
      }}
    </Query>
  </div>
);
